/* ═══════════════════════════════════════════════════════════════
   MeetingMode — Mode 3: Friday full-screen row-by-row walkthrough.
   Lean v1 per contract: review + one-key confirm only, no editing, no
   gadgets. Big type, ↓/↑ row nav, current row spotlighted, "Time's
   good ✓" stamps who/when (forecast_confirms), progress "N of M",
   exit summary of who's still unconfirmed.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import type { TeamMember, Project, HourForecast, ForecastConfirm } from '../../../shared/types';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import EmptyState from '../../components/EmptyState';
import { addDays, formatLong, initialsOf, mondayOf } from './utils';

interface MemberWeekSummary {
  member: TeamMember;
  total: number;
  vacationHours: number;
  pairings: { label: string; hours: number; color?: string }[];
  confirm: ForecastConfirm | null;
}

export function MeetingMode({
  members,
  projects,
  forecasts,
  confirms,
  weekStart,
  onWeekChange,
  onExit,
  onConfirm,
  canConfirm,
}: {
  members: TeamMember[];
  projects: Project[];
  forecasts: HourForecast[];
  confirms: ForecastConfirm[];
  weekStart: string;
  onWeekChange: (next: string) => void;
  onExit: () => void;
  onConfirm: (memberId: string) => Promise<void>;
  canConfirm: (memberId: string) => boolean;
}) {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState<'walkthrough' | 'summary'>('walkthrough');
  const [confirming, setConfirming] = useState(false);

  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const confirmByMember = useMemo(
    () => new Map(confirms.map((c) => [c.teamMemberId, c])),
    [confirms]
  );

  const rows: MemberWeekSummary[] = useMemo(() => {
    return members
      .map((m) => {
        const own = forecasts.filter(
          (f) => f.teamMemberId === m.id && mondayOf(f.dayDate || f.weekStartDate) === weekStart
        );
        const byProject = new Map<string, number>();
        let vacationHours = 0;
        let total = 0;
        for (const f of own) {
          const h = Number(f.hours) || 0;
          total += h;
          if (f.sourceKind === 'vacation') {
            vacationHours += h;
            continue;
          }
          if (f.projectId) byProject.set(f.projectId, (byProject.get(f.projectId) || 0) + h);
        }
        const pairings = Array.from(byProject.entries())
          .map(([pid, hours]) => ({
            label: projectById.get(pid)?.name || 'Unknown project',
            hours,
          }))
          .sort((a, b) => b.hours - a.hours);
        return {
          member: m,
          total,
          vacationHours,
          pairings,
          confirm: confirmByMember.get(m.id) || null,
        };
      })
      .sort((a, b) => a.member.name.localeCompare(b.member.name));
  }, [members, forecasts, weekStart, projectById, confirmByMember]);

  useEffect(() => {
    setIdx(0);
  }, [weekStart]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (phase !== 'walkthrough') return;
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        setIdx((i) => Math.min(rows.length - 1, i + 1));
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        setIdx((i) => Math.max(0, i - 1));
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setPhase('summary');
      } else if ((e.key === ' ' || e.key === 'c' || e.key === 'Enter') && current) {
        e.preventDefault();
        void confirmCurrent();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, idx, rows]);

  const current = rows[idx];

  async function confirmCurrent() {
    if (!current || !canConfirm(current.member.id) || confirming) return;
    setConfirming(true);
    try {
      await onConfirm(current.member.id);
      setIdx((i) => Math.min(rows.length - 1, i + 1));
    } finally {
      setConfirming(false);
    }
  }

  if (phase === 'summary') {
    const unconfirmed = rows.filter((r) => !r.confirm);
    return (
      <div style={overlayStyle}>
        <div style={{ maxWidth: rem(560), margin: `${rem(80)} auto`, padding: 'var(--space-5)' }}>
          <h2
            style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: 700,
              color: 'var(--tx)',
              marginBottom: 'var(--space-1)',
            }}
          >
            Meeting summary — {formatLong(weekStart)}
          </h2>
          <p
            style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)', marginBottom: rem(20) }}
          >
            {rows.length - unconfirmed.length} of {rows.length} confirmed.
          </p>
          {unconfirmed.length === 0 ? (
            <div
              style={{
                color: 'var(--color-success)',
                fontSize: 'var(--font-size-md)',
                fontWeight: 600,
              }}
            >
              Everyone's confirmed for this week.
            </div>
          ) : (
            <div>
              <div
                style={{
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-2)',
                  textTransform: 'uppercase',
                }}
              >
                Still unconfirmed
              </div>
              <ul
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: rem(6),
                }}
              >
                {unconfirmed.map((r) => (
                  <li
                    key={r.member.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 'var(--font-size-base)',
                      color: 'var(--tx)',
                      padding: `${rem(6)} ${rem(10)}`,
                      background: 'var(--sa)',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <span>{r.member.name}</span>
                    <span style={{ color: 'var(--tm)' }}>
                      {r.total > 0 ? `${r.total}h planned` : 'nothing planned'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ display: 'flex', gap: rem(10), marginTop: 'var(--space-5)' }}>
            <Button variant="outline" onClick={() => setPhase('walkthrough')}>
              Back to walkthrough
            </Button>
            <Button onClick={onExit}>Done</Button>
          </div>
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={overlayStyle}>
        <div style={{ maxWidth: rem(480), margin: `${rem(120)} auto` }}>
          <EmptyState
            title="Nobody to review"
            subtitle="Widen scope in Plan mode before starting a meeting walkthrough."
          />
          <div style={{ textAlign: 'center', marginTop: rem(16) }}>
            <Button variant="outline" onClick={onExit}>
              Exit
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={overlayStyle}
      role="dialog"
      aria-label="Forecasting meeting walkthrough"
      aria-modal="true"
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: rem(12),
            padding: `${rem(14)} var(--space-5)`,
            borderBottom: '1px solid var(--bd)',
          }}
        >
          <Button variant="ghost" size="sm" onClick={() => onWeekChange(addDays(weekStart, -7))}>
            ‹ prev wk
          </Button>
          <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--tx)' }}>
            {formatLong(weekStart)}
          </span>
          <Button variant="ghost" size="sm" onClick={() => onWeekChange(addDays(weekStart, 7))}>
            next wk ›
          </Button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 'calc(var(--font-size-base) * 12 / 13)', color: 'var(--tm)' }}>
            {idx + 1} of {rows.length}
          </span>
          <Button variant="outline" size="sm" onClick={() => setPhase('summary')}>
            <Icon icon={X} em={1.4} /> Exit
          </Button>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-5)',
          }}
        >
          {current && (
            <div style={{ width: '100%', maxWidth: rem(640), textAlign: 'center' }}>
              <div
                aria-hidden="true"
                style={{
                  width: rem(96),
                  height: rem(96),
                  borderRadius: '50%',
                  background: 'var(--ac)',
                  color: 'var(--color-text-inverse)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: rem(32),
                  fontWeight: 700,
                  margin: `0 auto ${rem(16)}`,
                }}
              >
                {initialsOf(current.member.name)}
              </div>
              <div style={{ fontSize: rem(34), fontWeight: 800, color: 'var(--tx)' }}>
                {current.member.name}
              </div>
              <div
                style={{
                  fontSize: rem(20),
                  color: 'var(--tm)',
                  marginTop: rem(6),
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {current.total}h planned
                {current.vacationHours > 0 && ` (incl. ${current.vacationHours}h vacation)`}
              </div>

              <div
                style={{
                  marginTop: 'var(--space-5)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                  maxWidth: rem(420),
                  margin: `${rem(24)} auto 0`,
                }}
              >
                {current.pairings.length === 0 && (
                  <div style={{ fontSize: 'var(--font-size-md)', color: 'var(--tm)' }}>
                    Nothing forecasted this week.
                  </div>
                )}
                {current.pairings.map((p) => (
                  <div
                    key={p.label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: rem(15),
                      padding: `var(--space-2) ${rem(14)}`,
                      background: 'var(--sa)',
                      borderRadius: rem(8),
                    }}
                  >
                    <span>{p.label}</span>
                    <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {p.hours}h
                    </span>
                  </div>
                ))}
              </div>

              {current.confirm ? (
                <div
                  style={{
                    marginTop: rem(28),
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--color-success)',
                    fontWeight: 600,
                  }}
                >
                  <Icon icon={Check} em={1.0769} style={{ verticalAlign: rem(-2) }} /> Confirmed{' '}
                  {new Date(current.confirm.confirmedAt).toLocaleString()}
                </div>
              ) : (
                <Button
                  size="lg"
                  onClick={() => void confirmCurrent()}
                  disabled={!canConfirm(current.member.id) || confirming}
                  style={{
                    marginTop: rem(28),
                    fontSize: 'var(--font-size-lg)',
                    padding: `${rem(14)} ${rem(28)}`,
                  }}
                >
                  <Icon icon={Check} size="md" /> Time's good {confirming ? '…' : ''}
                </Button>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'var(--space-2)',
            padding: `${rem(14)} 0 ${rem(22)}`,
          }}
        >
          <Button
            variant="outline"
            size="icon"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            aria-label="Previous person"
          >
            <Icon icon={ChevronUp} em={1.2308} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setIdx((i) => Math.min(rows.length - 1, i + 1))}
            aria-label="Next person"
          >
            <Icon icon={ChevronDown} em={1.2308} />
          </Button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--bg)',
  zIndex: 1000,
  overflow: 'auto',
};
